import React, { Component } from 'react';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

class Modal extends Component {
  state = {
    maxHeight: window.innerHeight,
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleKeyPress);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
    window.removeEventListener('keydown', this.handleKeyPress, false);
  }

  handleClick = (event) => {
    // Some dialogs ask a question whose answers differ - "log in" and "start a new project" are
    // not the same outcome - so a stray click on the background must not pick one of them.
    if (this.props.requireChoice) return;

    // Close the modal if the background is clicked
    // $ is required as IE11 doesn't support native .closest()
    if ($(event.target).closest('.modal-wrapper').length === 0) {
      this.props.onClose();
    }
  };

  handleResize = debounce(() => {
    this.setState({ maxHeight: window.innerHeight });
  }, 500);

  handleKeyPress = (event) => {
    // On escape
    if (event.keyCode === 27 && !this.props.requireChoice) {
      this.props.onClose();
    }
  };

  get modalMaxHeight() {
    if (!this.props.maxHeight) {
      return this.state.maxHeight - 32;
    }

    return Math.min(this.props.maxHeight, this.state.maxHeight) - 32;
  }

  render() {
    if (!this.props.open) return null;

    const { turnOffBackgroundClose } = this.props;

    const onClick = !turnOffBackgroundClose ? this.handleClick : undefined;

    const modalBackgroundProps = {
      className: classNames(
        'modal-background d-flex align-items-center h-100 w-100 position-fixed',
        this.props.backgroundClassName,
      ),
      onClick,
      style: {
        overflow: 'scroll', // for mWeb Android ability to scroll envelope address modal
        maxHeight: this.state.maxHeight,
        // Set inline rather than in a stylesheet: .modal-background's own
        // z-index is declared inside a nested `.builder .desktop` import, so a
        // class-based override is at the mercy of import order and nesting.
        // A dialog opened from inside another fixed overlay has to be able to
        // state its stacking outright.
        ...(this.props.zIndex === undefined
          ? {}
          : { zIndex: this.props.zIndex }),
      },
    };

    const modalProps = {
      className: 'modal-wrapper d-flex flex-column',
      style: {
        maxHeight: this.modalMaxHeight,
        maxWidth: this.props.maxWidth ? `${this.props.maxWidth}px` : '100%',
      },
    };

    const $title = (({ title }) => {
      if (title && typeof title === 'string' && title) {
        return <p className="lead">{title}</p>;
      }

      return title;
    })(this.props);

    return (
      <FocusTrap focusTrapOptions={{ returnFocusOnDeactivate: true }}>
        <div {...modalBackgroundProps} data-test-id="modal-background">
          <div
            {...modalProps}
            role={this.props.actions ? 'dialog' : 'alert'}
            aria-labelledby="modal_title"
            aria-modal="true"
            aria-hidden={!this.props.open}>
            <div
              className={classNames(
                'd-flex flex-column text-left',
                this.props.className,
              )}>
              {this.props.close && (
                <div className="heading clearfix close-modal">
                  <button
                    className="close float-right"
                    aria-label="Close"
                    onClick={this.props.onClose}>
                    <i className="skinny-icon close" />
                  </button>
                </div>
              )}
              <div
                role="heading"
                aria-level="2"
                className="modal-title"
                id="modal_title">
                {$title}
              </div>
              <div className="modal-body">{this.props.children}</div>
              <div
                className={classNames('modal-actions', {
                  'd-none': !this.props.actions,
                })}>
                {this.props.actions}
              </div>
            </div>
          </div>
        </div>
      </FocusTrap>
    );
  }
}

Modal.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.bool,
  // Suppresses the background-click and escape dismissals, so the reader has to answer.
  requireChoice: PropTypes.bool,
  actions: PropTypes.element,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  turnOffBackgroundClose: PropTypes.bool,
  // Lets a modal opened from inside another fixed overlay raise its own
  // backdrop above that overlay.
  backgroundClassName: PropTypes.string,
  zIndex: PropTypes.number,
};

Modal.defaultProps = {
  turnOffBackgroundClose: null,
};

export default Modal;
